/**
 * Handicap trend sparkline — tiny SVG line chart of a player's score
 * differentials over their last N rounds.
 *
 * Pure component: hand it an array of numbers (most recent last) and it
 * renders a compact chart with min/max labels. Colour comes from the
 * primary CSS variable so it inherits the app theme.
 */
export default function HandicapSparkline({ diffs, height = 40, "data-testid": testId }) {
  if (!diffs || diffs.length < 2) {
    return (
      <div
        className="text-[10px] italic text-[var(--text-secondary)] py-1"
        data-testid={testId ? `${testId}-empty` : "handicap-sparkline-empty"}
      >
        Need at least 2 eligible rounds to draw a trend line.
      </div>
    );
  }

  const values = diffs.map((d) => Number(d)).filter((n) => Number.isFinite(n));
  if (values.length < 2) return null;

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  // Normalise to a 100-wide viewBox — height comes from prop for flexibility.
  const stepX = 100 / (values.length - 1);
  const points = values
    .map((v, i) => {
      const x = i * stepX;
      // Invert Y so lower diffs (better rounds) sit HIGHER on the chart —
      // the direction people intuitively read as "improvement".
      const y = ((max - v) / range) * 100;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");

  // Trend arrow: newest vs oldest — if lower now than then, improving.
  const trendDown = values[values.length - 1] < values[0];

  return (
    <div className="w-full" data-testid={testId || "handicap-sparkline"}>
      <div className="flex items-baseline justify-between text-[10px] mb-1 text-[var(--text-secondary)]">
        <span>
          Best <span className="font-mono-num font-bold text-green-300">{min.toFixed(1)}</span>
        </span>
        <span data-testid="handicap-sparkline-trend">
          {trendDown ? "↘ trending better" : "↗ higher lately"}
        </span>
        <span>
          Worst <span className="font-mono-num font-bold text-[var(--text-secondary)]">{max.toFixed(1)}</span>
        </span>
      </div>
      <svg
        viewBox={`0 0 100 100`}
        preserveAspectRatio="none"
        style={{ width: "100%", height: `${height}px` }}
        role="img"
        aria-label={`Handicap trend over last ${values.length} rounds`}
      >
        {/* Gridline at the midpoint for a visual reference. */}
        <line x1="0" y1="50" x2="100" y2="50" stroke="rgba(255,255,255,0.08)" strokeWidth="0.5" />
        {/* Trend line. */}
        <polyline
          fill="none"
          stroke="var(--primary)"
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
          points={points}
        />
        {/* End-point dot so eyes land on the most recent value. */}
        {(() => {
          const last = values[values.length - 1];
          const cx = 100;
          const cy = ((max - last) / range) * 100;
          return (
            <circle
              cx={cx}
              cy={cy}
              r="2.5"
              fill="var(--primary)"
              stroke="var(--bg-app)"
              strokeWidth="1"
            />
          );
        })()}
      </svg>
    </div>
  );
}
